import { FC, useMemo, useRef } from 'react'
import { default as LexiconComponent } from 'src/components/Lexicon'
import { useTranslation } from 'react-i18next'
import { LEXICON_PER_PAGE } from 'src/constant'
import { Lexicon } from 'src/rest/types/Lexicon'
import { limitOffset } from 'src/helpers/ArrayHelpers'
import { useRouter } from 'next/router'
import Router from 'src/routes/Router'
import routes from 'src/routes/definitions'
import { FormikProps } from 'formik'
import _ from 'lodash'

import { BlockProps } from '../props'
import { LexiconProps } from '../../../components/Lexicon/index'

import { BlocLexiqueProps } from './props'

const BlocLexique: FC<BlockProps<BlocLexiqueProps>> = ({
  configuration,
  locale,
}) => {
  const { lexicons } = configuration
  const { t } = useTranslation()
  const router = useRouter()
  const formikRef = useRef<FormikProps<any>>(null)

  const page = useMemo<number>(
    () => (router.query?.page ? Number(router.query?.page) || 0 : 1),
    [router]
  )
  const term = useMemo<string>(
    () => (router.query?.search ? String(router.query?.search) : ''),
    [router]
  )
  const filters = useMemo<Array<any>>(
    () =>
      router.query.filters && typeof router.query.filters === 'string'
        ? router.query.filters.split(',')
        : [],
    [router]
  )

  const letters = useMemo(() => {
    return lexicons.reduce((acc, next) => {
      const letter = next.word.toLocaleLowerCase().charAt(0)
      return acc.includes(letter) ? acc : [...acc, letter]
    }, [] as Array<string>)
  }, [lexicons])

  const filteredLexicons = useMemo(() => {
    return lexicons
      .filter((item) =>
        filters.length > 0
          ? filters.includes(item.word.toLocaleLowerCase().charAt(0))
          : true
      )
      .filter((item) =>
        item.word.toLocaleLowerCase().startsWith(term.toLocaleLowerCase())
      )
  }, [lexicons, term, filters])

  const paginatedLexicons = useMemo(() => {
    return limitOffset(
      filteredLexicons,
      LEXICON_PER_PAGE,
      (page - 1) * LEXICON_PER_PAGE
    ).reduce(
      (acc, next) => {
        const letter = next.word.trim().charAt(0)

        if (Reflect.has(acc, letter)) {
          Reflect.set(acc, letter, [...acc[letter], { ...next }])
        } else {
          Reflect.set(acc, letter, [{ ...next }])
        }

        return acc
      },
      {} as Record<string, Array<Lexicon>>
    )
  }, [filteredLexicons, page])

  const props: LexiconProps = useMemo(
    () => ({
      letters: Object.entries(paginatedLexicons).map(([letter, items]) => ({
        letter,
        items: items.map((item) => ({
          title: item.word,
          richTextProps: { html: item.definition },
        })),
      })),
      searchAndFilterProps: {
        texts: {
          placeholder: t('lexiconsPage.search.texts.placeholder'),
          submitLabel: t('lexiconsPage.search.texts.submitLabel'),
          legend: t('lexiconsPage.search.texts.legend'),
        },
        formikFormProps: {
          innerRef: formikRef,
          enableReinitialize: true,
          onContextUpdate: (c) => console.log('c', c.values),
          onSubmit: (v) => {
            Router.pushRoute(
              routes.lexicons,
              {
                ...(v.term &&
                  _.isEqual(v.filters ?? [], filters) && {
                    search: v.term,
                  }),
                ...(v.filters?.length &&
                  term === v.term && {
                    filters: v.filters,
                  }),
              },
              { locale, scroll: false }
            )
          },
          initialValues: {
            term: (router.query?.search as string) ?? '',
            filters,
          },
        },
        filters: letters.map((letter) => ({
          label: letter.toUpperCase(),
          value: letter.toLowerCase(),
          onClick: () => formikRef?.current?.submitForm(),
        })),
      },
      paginationProps: {
        pageCount: Math.ceil(filteredLexicons.length / LEXICON_PER_PAGE),
        pageRangeDisplayed: 2,
        marginPagesDisplayed: 2,
        onPageChange: (value) => {
          Router.pushRoute(
            routes.lexicons,
            {
              ...(term && {
                search: term,
              }),
              ...(filters.length && {
                filters: filters,
              }),
              page: value.selected + 1,
            },
            { locale, scroll: false }
          )
        },
        texts: {
          prev: t('lexiconsPage.pagination.texts.prev'),
          next: t('lexiconsPage.pagination.texts.next'),
        },
      },
    }),
    [
      filteredLexicons,
      filters,
      letters,
      locale,
      paginatedLexicons,
      router,
      t,
      term,
    ]
  )

  return <LexiconComponent {...props} />
}

export default BlocLexique
